import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import SectionHeader from "@/components/layout/SectionHeader";
import { Star, ArrowRight, MapPin, Timer } from "lucide-react";

export function DealCard({ deal }) {
  return (
    <a
      href={deal.affiliate_url}
      target="_blank"
      rel="noopener sponsored"
      data-testid={`deal-card-${deal.title.toLowerCase().replace(/\s+/g, "-")}`}
      className="group relative block bg-white rounded-2xl border border-slate-200 overflow-hidden card-hover"
    >
      <div className="relative h-52 overflow-hidden">
        <img src={deal.image} alt={deal.title} className="w-full h-full object-cover img-zoom" loading="lazy" />
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-accent text-white text-[11px] font-bold uppercase tracking-wider">
          {deal.discount_percent}% OFF
        </div>
        <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-md bg-white/95 text-slate-800 text-[11px] font-semibold inline-flex items-center gap-1">
          <Star size={11} className="text-accent fill-accent" /> {deal.rating}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider mb-1.5">
          <MapPin size={11} /> {deal.destination} · <Timer size={11} /> {deal.duration}
        </div>
        <h3 className="font-montserrat font-bold text-lg text-slate-900">{deal.title}</h3>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {(deal.tags || []).slice(0, 2).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-wider font-semibold text-brand bg-brand-50 px-2 py-1 rounded">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="text-[11px] text-slate-400 line-through">{fmtINR(deal.old_price)}</div>
            <div className="text-xl font-montserrat font-bold text-slate-900">{fmtINR(deal.new_price)}</div>
            <div className="text-[10px] text-slate-500">per person · via {deal.partner}</div>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:gap-2 transition-all">
            Book <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </a>
  );
}

export default function FeaturedDeals() {
  const [deals, setDeals] = useState([]);
  useEffect(() => { api.get("/deals?featured=true").then(({ data }) => setDeals(data)).catch(() => {}); }, []);

  if (!deals.length) return null;

  return (
    <section data-testid="featured-deals" className="bg-slate-50 py-16 md:py-24 border-y border-slate-200">
      <div className="container-page">
        <SectionHeader
          kicker="Featured Travel Deals"
          title="Save big on this week's handpicked offers"
          subtitle="Curated affiliate deals from Booking.com, Agoda, MakeMyTrip and Skyscanner. Compare and book in one click."
          cta={
            <Link to="/deals" data-testid="see-all-deals" className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1 ml-auto">
              See all deals <ArrowRight size={14} />
            </Link>
          }
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {deals.slice(0, 4).map((d) => <DealCard key={d.id} deal={d} />)}
        </div>
      </div>
    </section>
  );
}
